import { DataObject } from "../DataObject";
import { Checker } from "@/lib/Checker";
import { Time } from "@/lib/Time";

export class Project extends DataObject {
  // projectId optional
  constructor(path) {
    super({
      data: {
        // META
        name: "Untitled",
        userId: "",
        description: "",
        color: "",
        index: 1,
        isPrivate: false,
        // Project related
        type: Project.type.duration,
        timePeriod: Project.timePeriod.week,
        operator: Project.operator.more,
        target: 0,
        startTimestamp: new Date(),
        startUTCOffset: Time.date.getUTCOffSet(),
        // Touched by backend
        current: 0,
        totalValue: 0,
        totalCount: 0,
        updateTimestamp: new Date(),
        updateUTCOffset: Time.date.getUTCOffSet()
      },
      path: path,
      validator: data => {
        const {
          name,
          userId,
          description,
          color,
          index,
          isPrivate,
          type,

          timePeriod,
          operator,
          target,
          startTimestamp,
          startUTCOffset,

          current,
          totalValue,
          totalCount,
          updateTimestamp,
          updateUTCOffset
        } = data;
        Checker.checkTypes(name, ["string"], "name");
        if (name.length <= 0) Checker.throwDataError();
        Checker.checkTypes(userId, ["string"], "userId");
        Checker.checkTypes(description, ["string"], "description");
        Checker.checkTypes(color, ["string"], "color");
        Checker.checkTypes(index, ["number"], "index");
        Checker.checkTypes(type, ["string"], "type");
        Checker.checkTypes(isPrivate, ["boolean"], "isPrivate");
        if (index < 0 || index > 99) Checker.throwDataError();

        Checker.checkTypes(timePeriod, ["string"], "timePeriod");
        Checker.checkTypes(operator, ["string"], "operator");
        Checker.checkTypes(target, ["number"], "target");
        Checker.checkTypes(startTimestamp, [Date], "startTimestamp");
        Checker.checkTypes(startUTCOffset, ["number"], "startUTCOffset");
        Checker.checkTypes(current, ["number"], "current");
        Checker.checkTypes(totalValue, ["number"], "totalValue");
        Checker.checkTypes(totalCount, ["number"], "totalCount");
        Checker.checkTypes(updateTimestamp, [Date], "updateTimestamp");
        Checker.checkTypes(updateUTCOffset, ["number"], "updateUTCOffset");
      }
    });
  }

  addRecord(record) {
    this._handleTotal(record, true);
    this._handleRecord(record, true);
  }

  removeRecord(record) {
    this._handleTotal(record, false);
    this._handleRecord(record, false);
  }

  _handleRecord(record, isAdd) {
    if (
      !this._isSameTimePeriodWithNow(
        record.data.endTimestamp,
        record.data.UTCOffset
      )
    )
      return;
    if (
      !this._isSameTimePeriodWithLastUpdateInLocalTimezones(
        record.data.endTimestamp,
        record.data.UTCOffset
      )
    ) {
      // IsFailure takes into account of current timezone,
      // We used record timezone in the previous line,
      // so we should use !isSuccess()
      if (!this.isSuccess()) return;
      this.updateData({ current: 0 });
    }
    let delta = 0;
    if (record.data.type === "d" && this.type === Project.type.duration)
      delta = isAdd ? record.data.value : -record.data.value;
    if (record.data.type === "c" && this.type === Project.type.counter)
      delta = isAdd ? 1 : -1;
    if (delta !== 0) {
      this.updateData({ current: this._positiveSum(this.current, delta) });
    }
    if (isAdd) {
      this._updateTimestampAndUTCOffset(record);
    }
  }

  _handleTotal(record, isAdd) {
    let delta = 0;
    if (record.data.type === "d" && this.type === Project.type.duration)
      delta = isAdd ? record.data.value : -record.data.value;
    if (record.data.type === "c" && this.type === Project.type.counter)
      delta = isAdd ? 1 : -1;
    let countDelta = isAdd ? 1 : -1;
    this.updateData({
      totalValue: this._positiveSum(this.totalValue, delta)
    });
    this.updateData({
      totalCount: this._positiveSum(this.totalCount, countDelta)
    });
  }

  isCurrentValid() {
    return this._isSameTimePeriodWithLastUpdateInLocalTimezones(
      Time.date.getNow()
    );
  }

  _isSameTimePeriodWithLastUpdateInLocalTimezones(
    timestamp,
    UTCOffset = undefined
  ) {
    return Time.date.isSame(
      this.updateTimestamp,
      this.updateUTCOffset,
      timestamp,
      UTCOffset,
      this.timePeriod
    );
  }
  _isSameTimePeriodWithNow(timestamp, UTCOffset = undefined) {
    return Time.date.isSame(
      Time.date.getNow(),
      UTCOffset,
      timestamp,
      UTCOffset,
      this.timePeriod
    );
  }

  _updateTimestampAndUTCOffset(record) {
    this.updateData({
      updateTimestamp: record.data.endTimestamp,
      updateUTCOffset: record.data.UTCOffset
    });
  }

  _positiveSum(original, delta) {
    let t = original + delta;
    if (t < 0) t = 0;
    return t;
  }

  isPending() {
    return (
      !this.isSuccess() &&
      this.operator === Project.operator.more &&
      this._isSameTimePeriodWithLastUpdateInLocalTimezones(
        Time.date.getNow(),
        Time.date.getUTCOffSet()
      )
    );
  }

  isFailure() {
    return !this.isSuccess() && !this.isPending();
  }

  isSuccess() {
    if (this.operator === Project.operator.more) {
      return this.percentage >= 1;
    } else {
      return this.percentage <= 1;
    }
  }

  async restart() {
    this.updateData({
      startTimestamp: new Date(),
      startUTCOffset: Time.date.getUTCOffSet(),
      current: 0,
      updateTimestamp: new Date(),
      updateUTCOffset: Time.date.getUTCOffSet()
    });
    await this.save();
  }

  // Getters
  get blockStart() {
    return Time.date.getStartOf(
      Time.date.getNow(),
      this.timePeriod,
      Time.date.getUTCOffSet()
    );
  }

  get projectId() {
    let regex = /projects\/(.*)/;
    return this.path.match(regex).pop();
  }

  get blockEnd() {
    return Time.date.getEndOf(
      Time.date.getNow(),
      this.timePeriod,
      Time.date.getUTCOffSet()
    );
  }

  get percentage() {
    if (this.data.target === 0) {
      if (this.data.current === 0) {
        return 0;
      }
      return Infinity;
    } else {
      return this.data.current / this.data.target;
    }
  }

  get type() {
    return this.data.type;
  }

  get operator() {
    return this.data.operator;
  }

  get timePeriod() {
    return this.data.timePeriod;
  }

  get startTimestamp() {
    return this.data.startTimestamp;
  }

  get updateTimestamp() {
    return this.data.updateTimestamp;
  }

  get startUTCOffset() {
    return this.data.startUTCOffset;
  }

  get updateUTCOffset() {
    return this.data.updateUTCOffset;
  }

  get displayCurrent() {
    if (this.isFailure()) {
      return this.current;
    }
    if (this.isCurrentValid()) {
      return this.current;
    } else {
      return 0;
    }
  }

  get displayPercentage() {
    if (this.data.target === 0) {
      if (this.displayCurrent === 0) {
        return 0;
      }
      return Infinity;
    } else {
      return this.displayCurrent / this.data.target;
    }
  }

  get current() {
    return this.data.current;
  }

  get totalValue() {
    return this.data.totalValue;
  }
  get totalCount() {
    return this.data.totalCount;
  }

  get target() {
    return this.data.target;
  }

  get streak() {
    let streak = Time.date.diffBlock(
      this.updateTimestamp,
      this.updateUTCOffset,
      this.startTimestamp,
      this.startUTCOffset,
      this.timePeriod
    );
    if (this.isSuccess()) {
      if (this.operator === Project.operator.more) {
        streak += 1;
      } else {
        streak = Time.date.diffBlock(
          Time.date.getNow(),
          Time.date.getUTCOffSet(),
          this.startTimestamp,
          this.startUTCOffset,
          this.timePeriod
        );
      }
      return streak;
    } else {
      return streak;
    }
  }

  isDuration() {
    return this.data.type === Project.type.duration;
  }

  isCounter() {
    return this.data.type === Project.type.counter;
  }

  get name() {
    return this.data.name;
  }

  get index() {
    return this.data.index;
  }

  get color() {
    return this.data.color;
  }

  get description() {
    return this.data.description;
  }
}

Project.type = {
  duration: "d",
  counter: "c"
};

Project.operator = {
  more: ">=",
  less: "<="
};

Project.timePeriod = {
  day: "day",
  week: "isoWeek",
  month: "month"
};

Project.type = {
  duration: "d",
  counter: "c"
};
